<template>
    <div class="col-lg-8 mx-auto">
        <header class="text-center">
            <workflow-navigation workflowStage="Wants" />
            <div class="col-lg-10 mx-auto">
              <h2 class="skin-page-heading">What income do you want in retirement?</h2>
              <p>
                Answer the questions below to get a clearer picture of your retirement goals. You can change these answers 
                and see the effect as many times as you want.
              </p>
            </div>
        </header>

        <sub-section title="Your retirement income" :showHelp="false">
              <h3>{{ text('wants.tax-free-cash.title') }}</h3>
              <div class="mb-4">
                <options-question name="taxFreeLumpSum" :question="taxFreeCashQuestionText" :options="yesNoOptions"
                  v-model:selected="taxFreeLumpSumModel" :width="getYesNoWidth()">
                  <help title="Tax free cash" :text="taxFreeCashHelpText" />
                </options-question>
              </div>

                <h3>Increases after you retire</h3>

                <div class="mb-4">
                    <options-question name="increaseIncomeWithInflation"
                                      question="Do you want your income to increase after you retire?"
                                      :options="yesNoOptions"
                                      v-model:selected="increaseIncomeWithInflationModel"
                                      :width="getYesNoWidth()">
                        <help title="Increases" :text="HelpText.ReqmntInflation()" />
                    </options-question>
                </div>

                <div v-if="showIncreasesDuration" class="mb-4">
                    <options-question name="increaseIncomeWithInflationDuration"
                                      question="How long do you want it to increase for? Your whole life, or just until 75?"
                                      :options="increaseDurationOptions"
                                      v-model:selected="increaseIncomeWithInflationDurationModel"
                                      :width="getYesNoWidth()"
                                      :disabled="increaseIncomeWithInflationModel == 'No'">
                        <help title="Increases duration" :text="HelpText.ReqmntInflationDuration()" />
                    </options-question>
                </div>

                <income-guidance v-if="showIncomeGuiidance"
                                 ref="incomeGuidance"
                                 :show-description="!inNewUserSetupMode"
                                 :display-content="inNewUserSetupMode"
                                 @incomeGuidanceChanged="onIncomeGuidanceChanged">
                </income-guidance>

                <initial-income-setup ref="initialIncomeSetup"
                                      :visible="inNewUserSetupMode"
                                      :suggestedAmountOnPLSADate="suggestedAmountOnPLSADate"
                                      :baseIncome="suggestedAmountAtRetirement"
                                      :extraInitialIncome="0"
                                      @populate-income-plan="onPopulateIncomePlan">
                </initial-income-setup>

                <h3>Your income plan</h3>
                <div v-if="showExpensesUpgradeAlert" class="mb-4">
                    <div class="alert alert-success rounded" role="alert">
                        We've upgraded this side of Guiide. You can now specify the income you want for each year in your retirement
                        plan individually.
                        Please check that you are happy with the incomes that have been setup from your previous specification and make
                        any alterations you would like to build into your plan.
                    </div>
                </div>

                <p>
                    {{ incomePlanIntroText() }}
                </p>

                <div class="row pt-4 mb-4 mx-1">
                    <annual-expenses ref="annualExpenses"
                                     :is-new-user="inNewUserSetupMode"
                                     :inflation-increase-enabled="increaseWithInflation"
                                     :inflationAgeLimit="inflationAgeLimit">
                    </annual-expenses>
                </div>

                <p>
                    {{ text('wants.shortfall.description') }}
                </p>

            <bad-response-viewer ref="badResponseViewer" />
        </sub-section>

        <page-nav backLink="lifeexpectancy" @nextClicked="onNextClicked" :parent-will-handle-next="true" :next-link="nextLink" />

        <contact-us class="mt-5"></contact-us>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick, onMounted } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import OptionsQuestion from "@/guiide/components/common/OptionsQuestion.vue";
import IncomeGuidance from "./wants/IncomeGuidance.vue";
import InitialIncomeSetup from "./wants/InitialIncomeSetup.vue";
import AnnualExpenses from "./wants/AnnualExpenses.vue";
import BadResponseViewer from "@/shared/components/BadResponseViewer.vue";
import ContactUs from "./common/ContactUs.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import { Retiree } from "../ts/retiree";
import { app } from "../ts/app";
import ResponseTracker from "@/shared/ts/responseTracker";
import { MessageResponse } from "../ts/response/messageResponse";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";
import { createModernIncomeArray } from "../ts/wants/wantsUtilities";
import { SuggestedAmounts } from "../ts/wants/suggestedAmounts";
import { yesNoOptions, mapBooleanToYesNo, mapYesNoToBoolean, getYesNoWidth } from "../ts/utilities";
import { useStore } from "vuex";
import { InitialIncomeProfile } from "../ts/wants/initialIncomeProfile";
import Constants from "../ts/constants";
import { IncomeGuidanceAnswers } from "../ts/wants/incomeGuidanceAnswers";
import Lifestyle from "../ts/lifestyle";
import { formatter } from "@/shared/ts/formatter";
import Monitoring from "../ts/monitoring/monitoring";
import { useUIContent } from "../ts/configuration/useUIContent";

const store = useStore();
const { text, templateHtmlArray } = useUIContent();

const increaseDurationOptions = [Constants.InflationDurationForLife, "75"];

const suggestedAmountOnPLSADate = ref(0);
const suggestedAmountAtRetirement = ref(0);
const showExpensesUpgradeAlert = ref(false);
const taxFreeLumpSumModel = ref("Yes");
const increaseIncomeWithInflationModel = ref("Yes");
const increaseIncomeWithInflationDurationModel = ref(Constants.InflationDurationForLife);

const incomeGuidance = ref(null);
const initialIncomeSetup = ref(null);
const annualExpenses = ref(null);
const badResponseViewer = ref(null);
const nextLink = ref("");

const increaseWithInflation = computed<boolean>(() => mapYesNoToBoolean(increaseIncomeWithInflationModel.value));
const inflationAgeLimit = computed<number>(() => {
  const lifeAsAnAge = 200;

  if (!store.state.configuration.requirementsExpenseEditPriviledges) {
    return lifeAsAnAge;
  }

  return increaseIncomeWithInflationDurationModel.value == Constants.InflationDurationForLife ? lifeAsAnAge : 75;
});

onMounted(async() => {
  const nextWorkflowStep = await workflow.getNextWorkflowStep(View.Requirements);
  nextLink.value = nextWorkflowStep.resource;
});

watch(() => store.state.retiree, (retiree: Retiree) => {
  if (retiree == null) {
    return;
  }

  taxFreeLumpSumModel.value = mapBooleanToYesNo(
    retiree.requirements.takingTaxFreeAsLumpSum
  );

  increaseIncomeWithInflationModel.value = mapBooleanToYesNo(
    retiree.requirements.incomeIncreasesWithInflation
  );

  increaseIncomeWithInflationDurationModel.value = retiree.requirements.incomeIncreasesWithInflationDuration;

  const userHasAlreadyDefinedAmount = retiree.requirements.annualAfterTaxIncome != 0;
  const userHasNotDefinedModernExpenses = retiree.requirements.income.length == 0;

  if (!inNewUserSetupMode.value && userHasAlreadyDefinedAmount && userHasNotDefinedModernExpenses) {
    const totalPlanYears = retiree.lifeExpectancy;

    const annualBaseIncome = retiree.requirements.annualAfterTaxIncome;
    const extraFirstFiveYearsIncome = retiree.requirements.extraIncomeFirstNYears;
    const extraIncomeYears = 5;
    const defaultIncomeArray = createModernIncomeArray(
      annualBaseIncome,
      extraFirstFiveYearsIncome,
      extraIncomeYears,
      totalPlanYears,
      Constants.IncomeStepSize);

    nextTick(() => {
      annualExpenses.value.setIncome(defaultIncomeArray);
      const onlyShowGuiideUsersTheUpgradeMessage = store.state.configuration.requirementsExpenseEditPriviledges;
      showExpensesUpgradeAlert.value = onlyShowGuiideUsersTheUpgradeMessage;
    });

  }
});

const taxFreeCashQuestionText = computed<string>(() => {
  return store.state.retiree?.alreadyDrawingPension
    ? "When you started taking your defined contribution pension pots, did you take 25% as tax free cash straight away?"
    : "Do you want to take 25% of your defined contribution pension pots as a tax free lump sum at retirement?";
});

const taxFreeCashHelpText = computed<Array<string>>(() => {
  const taxFreeCashLimit = formatter.formatCurrency(Constants.TaxFreeCashLimit);
  return templateHtmlArray("wants.tax-free-cash.help.body", { taxFreeCashLimit });
});

const inNewUserSetupMode = computed<boolean>(() => {
  const retiree = store.state.retiree;

  if (retiree == null) {
    return false;
  }

  return !retiree.isRegistered;
});

const showIncomeGuiidance = computed<boolean>(() => {
  if (inNewUserSetupMode.value) {
    return true;
  }

  const retiree = store.state.retiree;
  if (retiree == null) {
    return true;
  }

  return !retiree.alreadyDrawingPension;
});

const showIncreasesDuration = computed<boolean>(() => {
  return store.state.configuration.requirementsExpenseEditPriviledges;
});

const incomePlanIntroText = function () {
  return store.state.configuration.requirementsExpenseEditPriviledges
    ? "If you want to get really detailed, you can change the income in any year below by pressing edit."
    : "You can see the income you plan to receive in each year of your retirement below.";
}

function onIncomeGuidanceChanged(guidance: SuggestedAmounts) {
  suggestedAmountOnPLSADate.value = guidance.suggestedAmountOnPLSADate;
  suggestedAmountAtRetirement.value = guidance.suggestedAmountAtRetirement;
}

async function onPopulateIncomePlan(incomeDetail: InitialIncomeProfile) {
  const guidanceAnswers = getIncomeGuidanceAnswers();
  Monitoring.trackEvent('generateIncomePlan', {
    retirementType: guidanceAnswers.lifestyle,
    afterTaxIncome: incomeDetail.baseIncome,
    extraIncome: incomeDetail.extraIncome,
    extraIncomeYears: incomeDetail.extraIncomeYears,
  });

  const retiree: Retiree = store.state.retiree;
  const totalPlanYears = retiree.lifeExpectancy;

  const defaultIncomeArray = createModernIncomeArray(
    incomeDetail.baseIncome,
    incomeDetail.extraIncome,
    incomeDetail.extraIncomeYears,
    totalPlanYears,
    Constants.IncomeStepSize);

  await annualExpenses.value.setIncome(defaultIncomeArray);
}

async function onNextClicked() {
  badResponseViewer.value.clear();

  const takingDCTaxFreeAsLumpSum = mapYesNoToBoolean(taxFreeLumpSumModel.value);
  const increaseIncomeWithInflation = mapYesNoToBoolean(increaseIncomeWithInflationModel.value);
  const guidanceAnswers = getIncomeGuidanceAnswers();
  const oldStyleIncome: { baseIncome: number, extraIncome: number } = initialIncomeSetup.value.getIncome();
  const income = annualExpenses.value.getIncome();

  const newUserHasntPopulatedPlan = inNewUserSetupMode.value && income.every(i => i == 0);
  if (newUserHasntPopulatedPlan) {
    badResponseViewer.value.update(false, "Please click the 'Generate your income plan' button to create your income plan.");
    return;
  }

  const responseTracker: ResponseTracker<MessageResponse> = await app.updateRequirements(
    takingDCTaxFreeAsLumpSum,
    guidanceAnswers.livesInLondon,
    guidanceAnswers.supportingPartner,
    guidanceAnswers.lifestyle,
    oldStyleIncome.baseIncome,
    oldStyleIncome.extraIncome,
    increaseIncomeWithInflation,
    increaseIncomeWithInflationDurationModel.value,
    income
  );

  if (responseTracker.isOk) {
    await workflow.goToNextWorkflowStep();
  } else {
    if (responseTracker.trackerWillHandleFailure) {
      responseTracker.handleFailure();
    } else {
      badResponseViewer.value.update(
        responseTracker.isOk,
        responseTracker.message
      );
    }
  }
}

function getIncomeGuidanceAnswers(): IncomeGuidanceAnswers {
  const userAlreadyRetired = !showIncomeGuiidance.value;
  if (userAlreadyRetired) {
    const retiree: Retiree | null = store.state.retiree;
    if (retiree == null) {
      return { livesInLondon: false, supportingPartner: false, lifestyle: Lifestyle.Moderate };
    }
    return {
      livesInLondon: retiree.requirements.livesInLondon,
      supportingPartner: retiree.requirements.isSupportingPartner,
      lifestyle: retiree.requirements.targetLifestyle
    };
  }

  return incomeGuidance.value.getAnswers();
}
</script>

<style>
    section {
        padding-bottom: 16px;
    }

    h5 {
        margin-top: 15px;
        margin-bottom: 25px;
    }
</style>
